/* jshint esversion: 6 */
/**
 * CMSDataBridgeServiceModule is a service module that acts as a bridge between the CMS and other services.
 * It manages data storage, retrieval, and synchronization with backend services.
 */
class CMSDataBridgeServiceModule {
	/**
	 * Constructs the CMSDataBridgeServiceModule instance.
	 * @param {Object} LogBridgeService - Service for logging.
	 * @param {Object} HashMap - Data structure for storing key-value pairs.
	 * @param {Object} $signals - Signal emitter service.
	 * @param {Object} $http - Angular's HTTP service.
	 * @param {Object} $injector - Angular's dependency injector.
	 * @param {Object} $q - Angular's promise service.
	 * @param {Object} $translate - Translation service.
	 * @param {Object} $timeout - Angular's timeout service.
	 */
	constructor(LogBridgeService, HashMap, $signals, $http, $injector, $q, $translate, $timeout) {
		// 'ngInject'; // Remove this line if present
		this._logIdentifier = 'CMSDataBridgeService';
		this.$logger = LogBridgeService;
		this._savingList = [];
		this._removingList = [];
		this.VersioningAndLockingService = null;
		this.ContentObjectRegistryService = null;
		this.TaskDataService = null;
		this._commitMessages = {};
		this._hashMap = null;

		this.$logger.info(this, 'initialize');
	}

	/**
	 * Retrieves the bridge context, which is either the global jsBridgeResponseData or the internal hash map.
	 * @returns {Object} - The bridge context.
	 */
	getBridgeContext() {
		return window.jsBridgeResponseData || this._hashMap;
	}

	/**
	 * Initializes the response data.
	 * @param {boolean} useExistingData - Flag to determine if existing data should be used.
	 */
	initResponse(useExistingData) {
		this.$logger.logEnter(this, 'initResponse');
		console.error("CMS initResponse");

		this._ensureHashMap();

		this.$signals.emit(IB.Signals.RESPONSE_INITIALIZED, false);
	}

	/**
	 * Ensures that the hash map is initialized.
	 */
	_ensureHashMap() {
		if (!this._hashMap) {
			this._hashMap = new HashMap();
		}
	}

	/**
	 * Clears the hash map and resets the saving and removing lists.
	 */
	clear() {
		console.error("Clearing old", this._hashMap);
		if (angular.isObject(this._hashMap)) {
			this._hashMap.clear();
		}
		this._hashMap = new HashMap();
		this._removingList = [];
		this._savingList = [];
	}

	/**
	 * Generates a commit message for a given entry and method.
	 * @param {Object} entry - The entry for which to generate the commit message.
	 * @param {string} method - The method (e.g., 'put', 'removed').
	 * @returns {boolean|undefined} - Returns false if the entry is flagged as deleted and not saved, otherwise undefined.
	 */
	_generateCommitMessage(entry, method) {
		let uuid;
		if (!this.TaskDataService) {
			this.TaskDataService = this.$injector.get('TaskDataService');
		}
		let taskContext = this.TaskDataService.fetchContext();
		if (angular.isUUID(entry)) {
			let entryObj = taskContext.read(entry);
			uuid = entryObj.data.uuid;
		} else {
			uuid = entry.data.uuid;
		}
		if (entry.isFlaggedAsDeleted() && !entry.isSaved()) {
			return false;
		}
		if (entry.data.type === 'task') {
			return false;
		}

		let filterType = null,
			action = null,
			parentObjects = null,
			parentType = null,
			parentUUID = null,
			parentTitle = null,
			title = null;

		switch (entry.data.type) {
			case 'sheet':
				filterType = 'task';
				parentObjects = entry.getParentEntry(filterType);
				if (angular.isObject(parentObjects)) {
					parentType = parentObjects.type;
					parentUUID = parentObjects.uuid;
					parentTitle = parentObjects.title;
				}
				break;
			case 'layout':
				filterType = 'sheet';
				parentObjects = entry.getParentEntry(filterType);
				if (angular.isObject(parentObjects)) {
					parentType = parentObjects.type;
					parentUUID = parentObjects.uuid;
					parentTitle = parentObjects.useCustomTitle ? parentObjects.title : (this.$translate.instant("PREFIX.QUESTION") + ' ' + parentObjects.shortTitle);
				}
				break;
			case 'questionPart':
				filterType = 'sheet';
				parentObjects = entry.getParentEntry(filterType);
				let sheet = entry.getParentEntry('sheet');
				title = entry.data.useCustomTitle ? entry.data.title : (entry.data.isDefaultQuestionPart ? '' : sheet.shortTitle + '') + entry.data.shortTitle;
				if (angular.isObject(parentObjects)) {
					parentType = parentObjects.type;
					parentUUID = parentObjects.uuid;
					parentTitle = parentObjects.useCustomTitle ? parentObjects.title : (this.$translate.instant("PREFIX.QUESTION") + ' ' + parentObjects.shortTitle);
				}
				break;
			case 'contentObject':
				filterType = 'questionPart';
				parentObjects = entry.getParentEntry(filterType);
				sheet = entry.getParentEntry('sheet');
				if (angular.isObject(parentObjects)) {
					if (parentObjects.type === 'sheet') {
						parentType = parentObjects.type;
						parentUUID = parentObjects.uuid;
						parentTitle = parentObjects.useCustomTitle ? parentObjects.title : this.$translate.instant("PREFIX.QUESTION") + ' ' + parentObjects.shortTitle;
					} else {
						parentType = parentObjects.type;
						parentUUID = parentObjects.uuid;
						parentTitle = parentObjects.isDefaultQuestionPart ? parentObjects.shortTitle : (parentObjects.useCustomTitle ? parentObjects.title : sheet.shortTitle + '' + parentObjects.shortTitle);
					}
				}
				break;
			case 'container':
				filterType = 'questionPart';
				parentObjects = entry.getParentEntry(filterType);
				sheet = entry.getParentEntry('sheet');
				if (angular.isObject(parentObjects)) {
					if (parentObjects.type === 'sheet') {
						parentType = parentObjects.type;
						parentUUID = parentObjects.uuid;
						parentTitle = parentObjects.useCustomTitle ? parentObjects.title : this.$translate.instant("PREFIX.QUESTION") + '' + parentObjects.shortTitle;
					} else {
						parentType = parentObjects.type;
						parentUUID = parentObjects.uuid;
						parentTitle = parentObjects.isDefaultQuestionPart ? parentObjects.shortTitle : (parentObjects.useCustomTitle ? parentObjects.title : sheet.shortTitle + '' + parentObjects.shortTitle);
					}
				}
				break;
			case 'media':
				title = entry.getUploadName();
				break;
			default:
				parentType = null;
				parentUUID = null;
				parentTitle = null;
		}

		if (method === 'put') {
			if (entry.isSaved() && entry.isChanged()) {
				if (!angular.isArray(this._commitMessages[uuid])) {
					this._commitMessages[uuid] = [];
				}
				action = 'changed';
			} else if (!entry.isSaved()) {
				if (!angular.isArray(this._commitMessages[uuid])) {
					this._commitMessages[uuid] = [];
				}
				action = 'created';
			}
		} else if (method === 'removed') {
			if (!angular.isArray(this._commitMessages[uuid])) {
				this._commitMessages[uuid] = [];
			}
			action = 'removed';
		}

		if (action !== null) {
			this._commitMessages[uuid] = {
				action: action,
				type: entry.data.type,
				title: title ? title : entry.data.title ? entry.data.title : null,
				parentType: parentType,
				parentUUID: parentUUID,
				parentTitle: parentTitle,
			};
			if (entry.data.type === 'contentObject') {
				if (!this.ContentObjectRegistryService) {
					this.ContentObjectRegistryService = this.$injector.get('ContentObjectRegistryService');
				}
				let typeObj = this.ContentObjectRegistryService.getObjectProperties(entry.data.objectType);

				this._commitMessages[uuid].contentObject = this.$translate.instant(typeObj.objProperties.label);
			}
			this.$timeout(() => {
				entry.setAsSaved();

				if (entry.data.type === 'media' && action === 'removed') {
					taskContext.remove(uuid);
				}
			}, 100);
		}
	}

	/**
	 * Puts a key-value pair into the hash map and emits a signal.
	 * @param {string} key - The key to store the value under.
	 * @param {Object} value - The value to store.
	 * @param {Object} raw_data - The raw data associated with the value.
	 * @param {Function} callback - Callback function to handle the result.
	 * @returns {boolean} - Returns false if the entry is flagged as deleted and not saved.
	 */
	put(key, value, raw_data, callback) {
		this._ensureHashMap();
		this._hashMap.put(key, value);
		this.$signals.emit('put', key, JSON.stringify(value));

		if (raw_data !== undefined) {
			let data = {};
			data['entity_uuid'] = raw_data.uuid;
			data['entity_content'] = raw_data;

			if (!this.TaskDataService) {
				this.TaskDataService = this.$injector.get('TaskDataService');
			}
			let taskContext = this.TaskDataService.fetchContext(),
				entry = taskContext.read(raw_data.uuid);

			if (entry.isFlaggedAsDeleted() && !entry.isSaved()) {
				if (typeof callback == "function") {
					try {
						callback(true);
					} catch (error) {
						callback(false, error);
					}
				}
				return false;
			}
			this._generateCommitMessage(entry, 'put');

			let changedObj = JSON.parse(value);
			for (let attr in changedObj) {
				data['entity_content'][attr] = changedObj[attr];
			}

			if (typeof callback == "function") {
				try {
					this._savingList.push(data);
					callback(true);
				} catch (error) {
					callback(false, error);
				}
			} else {
				this._savingList.push(data);
			}
		}
	}

	/**
	 * Removes a key from the hash map and emits a signal.
	 * @param {string} key - The key to remove.
	 * @param {Function} callback - Callback function to handle the result.
	 * @returns {boolean} - Returns false if the entry is flagged as deleted and not saved.
	 */
	remove(key, callback) {
		if (typeof key !== 'undefined') {
			this._ensureHashMap();
			this._hashMap.remove(key);

			if (!this.TaskDataService) {
				this.TaskDataService = this.$injector.get('TaskDataService');
			}
			let taskContext = this.TaskDataService.fetchContext(),
				entry = taskContext.read(key);

			if (entry.isFlaggedAsDeleted() && !entry.isSaved()) {
				if (typeof callback == "function") {
					try {
						callback(true);
					} catch (error) {
						callback(false, error);
					}
				}
				return false;
			}

			this._generateCommitMessage(entry, 'removed');

			if (typeof callback == "function") {
				try {
					this._removingList.push(key);
					callback(true);
				} catch (error) {
					callback(false, error);
				}
			} else {
				this._removingList.push(key);
			}
		}
	}

	/**
	 * Commits the store by sending the saving and removing lists to the backend.
	 * @param {Function} callback - Callback function to handle the result.
	 */
	commitStore(callback) {
		let taskInfoService = this.$injector.get("TaskInfoService");
		let taskInfo = taskInfoService.getTaskInfo();
		let assessment_uuid = taskInfo.cms_getUUID();
		let $config = this.$injector.get("$config");
		let url = $config.cms_getAPIURL() + 'entity/' + assessment_uuid + '/';

		this.$http.defaults.headers.post['X-CSRFToken'] = angular.getCookie('csrftoken');

		let requestData = {
			savingList: this._savingList,
			removingList: this._removingList,
			commitMessages: this._commitMessages
		};

		this.$http.post(url, requestData).
			success((data, status, headers, config) => {
				if (typeof callback == "function") {
					callback(true, data);
				}

				if (!this.VersioningAndLockingService) {
					this.VersioningAndLockingService = this.$injector.get('VersioningAndLockingService');
				}
				if (angular.isFunction(this.VersioningAndLockingService.update)) {
					this.VersioningAndLockingService.update(data.objects.data);
				}

				this._savingList = [];
				this._removingList = [];

				this.$timeout(() => {
					this._commitMessages = {};
				}, 500);
			}).
			error((data, status, headers, config) => {
				if (typeof callback == "function") {
					callback(false, data);
				}
			});
	}

	/**
	 * Retrieves the value associated with a key from the hash map.
	 * @param {string} key - The key to retrieve the value for.
	 * @returns {Object} - The value associated with the key.
	 */
	get(key) {
		let result;
		this._ensureHashMap();
		result = this._hashMap.get(key);

		return result;
	}

	/**
	 * Retrieves the keys of the data stored in the hash map.
	 * @returns {Array} - An array of keys.
	 */
	getDataKeys() {
		let keys = [];
		if (this._hashMap) {
			keys = this._hashMap.keys();
		}
		return keys;
	}

	/**
	 * Dumps the contents of the hash map.
	 * @returns {Object} - The contents of the hash map.
	 */
	dump() {
		return this._hashMap.dump();
	}

	/**
	 * Retrieves the last persistence timestamp.
	 * @returns {number} - The last persistence timestamp.
	 */
	getLastPersistenceTS() {
		let timestamp = -1;
		if (this._hashMap) {
			timestamp = this._hashMap.getLatestAccessTime();
		}
		return timestamp;
	}

	/**
	 * Checks if there is existing data in the hash map.
	 */
	hasExistingData() {
		let hasData = this._hashMap !== undefined;
		this.$signals.emit("hasExistingDataResult", hasData, hasData, false);
	}

	/**
	 * Retrieves the video URL from the bridge context.
	 * @param {string} inAppUrl - The in-app URL.
	 * @returns {string} - The video URL.
	 */
	getVideoUrl(inAppUrl) {
		let videoUrl = inAppUrl;
		if (this.getBridgeContext()) {
			videoUrl = this.getBridgeContext().getVideoUrl(inAppUrl);
		}
		return videoUrl;
	}
}

// Add explicit dependency injection
CMSDataBridgeServiceModule.$inject = [
	'LogBridgeService',
	'HashMap',
	'$signals',
	'$http',
	'$injector',
	'$q',
	'$translate',
	'$timeout'
];

export default CMSDataBridgeServiceModule;